import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import api, { departmentsAPI, companiesAPI, delegationsAPI } from '../services/api';
import Button from '../components/ui/Button';
import Card, { StatCard } from '../components/ui/Card';
import Badge, { StatusBadge } from '../components/ui/Badge';
import Modal from '../components/ui/Modal';
import Input from '../components/ui/Input';
import Alert from '../components/ui/Alert';
import ConfirmModal from '../components/ui/ConfirmModal';
import SearchableSelect from '../components/ui/SearchableSelect';
import PageHeader from '../components/ui/PageHeader';
import Breadcrumbs from '../components/ui/Breadcrumbs';
import Tabs from '../components/ui/Tabs';
import ClmManagement from '../components/ClmManagement';
import Skeleton from '../components/ui/Skeleton';
import EmptyState from '../components/ui/EmptyState';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import HelpTooltip from '../components/ui/HelpTooltip';
import SignPasswordModal from '../components/SignPasswordModal';
import { useToast } from '../context/ToastContext';
import BrandingManager from '../components/BrandingManager';
import { usePageTour } from '../components/onboarding/PageTour';
import Employees from './Employees';
import AssignCourses from './AssignCourses';
import EmployeeSheets from './EmployeeSheets';
import CompanyDocuments from './CompanyDocuments';

// SVG Icons
const UsersIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
    />
  </svg>
);

const KeyIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
    />
  </svg>
);

const ArrowLeftIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
  </svg>
);

const PlusIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
  </svg>
);

const DocumentIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
    />
  </svg>
);

const BuildingIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
    />
  </svg>
);

// Sub-componentă: tab raport ITM — snapshot-uri oficiale + descărcări live (nesemnate)
const ITMReportTab = ({ company, companyId, showToast, user }) => {
  const { t } = useTranslation();
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [loadingZip, setLoadingZip] = useState(false);
  const [loadingGenerate, setLoadingGenerate] = useState(false);
  const [reports, setReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(true);
  const [signingId, setSigningId] = useState(null);
  const [pendingSignReportId, setPendingSignReportId] = useState(null);

  // Tour contextual pentru tab ITM. Hook-ul autoStart: pornește prima dată când userul ajunge
  // pe acest tab. Dacă elementele nu sunt încă în DOM, închide tour-ul singur (nu blochează).
  const itmTourSteps = [
    {
      selector: '[data-tour="itm-preview"]',
      titleKey: 'onboarding.tour.itm.preview.title',
      descKey: 'onboarding.tour.itm.preview.desc',
      placement: 'bottom',
    },
    {
      selector: '[data-tour="itm-official"]',
      titleKey: 'onboarding.tour.itm.official.title',
      descKey: 'onboarding.tour.itm.official.desc',
      placement: 'top',
    },
  ];
  const { tour: itmTour, startTour: startITMTour } = usePageTour({
    tourId: `itm-report-${companyId}`,
    steps: itmTourSteps,
  });

  // Rolul utilizatorului curent raportat la această firmă
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  const isOwnAdmin = user?.role === 'COMPANY_ADMIN' && user?.companyId === companyId;
  const canInitiateReport = isSuperAdmin || isOwnAdmin;
  const canSignAsAdmin = isSuperAdmin || isOwnAdmin;

  // SSM-ist: fie din parent pentru ext, fie din aceeași firmă cu flag pentru int
  const isExternalSSM =
    company?.parentCompanyId &&
    user?.companyId === company.parentCompanyId &&
    (user?.role === 'SSM_INSTRUCTOR' ||
      (user?.role === 'SSM_COMPANY_ADMIN' && user?.isSSMInstructor));
  const isInternalSSM =
    !company?.parentCompanyId && user?.companyId === companyId && user?.isSSMInstructor;
  const canSignAsSSM = isExternalSSM || isInternalSSM;

  const fetchReports = async () => {
    setLoadingReports(true);
    try {
      const res = await api.get(`/companies/${companyId}/itm-reports`);
      setReports(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingReports(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [companyId]);

  const triggerDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const safeCompanyName = (company?.name || 'firma').replace(/[^a-zA-Z0-9]/g, '-');

  const handleDownloadPDF = async () => {
    setLoadingPdf(true);
    try {
      const res = await api.get(`/companies/${companyId}/itm-report`, { responseType: 'blob' });
      triggerDownload(
        new Blob([res.data], { type: 'application/pdf' }),
        `raport-ITM-${safeCompanyName}.pdf`
      );
      showToast(t('itmReport.summaryDownloaded'), 'success');
    } catch (err) {
      showToast(err.response?.data?.error || t('itmReport.generateError'), 'error');
    } finally {
      setLoadingPdf(false);
    }
  };

  const handleDownloadZip = async () => {
    setLoadingZip(true);
    try {
      const res = await api.get(`/companies/${companyId}/itm-report/zip`, { responseType: 'blob' });
      triggerDownload(
        new Blob([res.data], { type: 'application/zip' }),
        `dosar-ITM-${safeCompanyName}.zip`
      );
      showToast(t('itmReport.zipDownloaded'), 'success');
    } catch (err) {
      showToast(err.response?.data?.error || t('itmReport.zipGenerateError'), 'error');
    } finally {
      setLoadingZip(false);
    }
  };

  const extractBlobError = async (err, fallback) => {
    if (err.response?.data instanceof Blob) {
      try {
        return JSON.parse(await err.response.data.text()).error || fallback;
      } catch (_) {}
    }
    return err.response?.data?.error || fallback;
  };

  const handleGenerateOfficial = async () => {
    setLoadingGenerate(true);
    try {
      const res = await api.post(`/companies/${companyId}/itm-reports`);
      showToast(res.data.message || t('itmReport.officialCreated'), 'success');
      await fetchReports();
    } catch (err) {
      showToast(err.response?.data?.error || t('itmReport.officialCreateError'), 'error');
    } finally {
      setLoadingGenerate(false);
    }
  };

  const handleDownloadReport = async (reportId, reportNumber) => {
    try {
      const res = await api.get(`/itm-reports/${reportId}/download`, { responseType: 'blob' });
      triggerDownload(new Blob([res.data], { type: 'application/pdf' }), `${reportNumber}.pdf`);
    } catch (err) {
      showToast(await extractBlobError(err, t('itmReport.downloadError')), 'error');
    }
  };

  const handleSignReport = (reportId) => {
    setPendingSignReportId(reportId);
  };

  const submitSignReport = async (password) => {
    const reportId = pendingSignReportId;
    if (!reportId) return;
    setSigningId(reportId);
    try {
      const res = await api.post(`/itm-reports/${reportId}/sign`, { password });
      showToast(res.data.message || t('itmReport.reportSigned'), 'success');
      setPendingSignReportId(null);
      await fetchReports();
    } catch (err) {
      const data = err.response?.data;
      if (data?.requiresAdESActivation) {
        setPendingSignReportId(null);
        showToast(data.error || t('itmReport.signError'), 'error');
        return;
      }
      throw err;
    } finally {
      setSigningId(null);
    }
  };

  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [reportsExpanded, setReportsExpanded] = useState(true);
  const handleDeleteReport = async () => {
    if (!deleteTargetId) return;
    try {
      await api.delete(`/itm-reports/${deleteTargetId}`);
      showToast(t('itmReport.reportDeleted'), 'success');
      setDeleteTargetId(null);
      await fetchReports();
    } catch (err) {
      showToast(err.response?.data?.error || t('itmReport.deleteError'), 'error');
    }
  };

  // Zile de când a fost creat raportul (pentru status pending)
  const daysSince = (dateStr) => {
    const diff = (Date.now() - new Date(dateStr).getTime()) / 86400000;
    return Math.floor(diff);
  };

  return (
    <div className="space-y-6">
      {itmTour}
      {/* #5: clarificăm rolul acestui loc — aici se SEMNEAZĂ raportul oficial; ansamblul e în meniu. */}
      <div className="rounded-lg border border-violet-200 dark:border-violet-800 bg-violet-50/60 dark:bg-violet-950/30 p-3 text-sm text-violet-900 dark:text-violet-200 flex items-start gap-2">
        <svg
          className="w-4 h-4 mt-0.5 flex-shrink-0"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
        <span>
          {t('itmReport.workspaceNote')}{' '}
          <Link
            to="/reports"
            className="underline font-medium hover:text-violet-700 dark:hover:text-violet-100"
          >
            {t('itmReport.overviewLink')}
          </Link>
        </span>
      </div>
      <Card>
        <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 inline-flex items-center gap-2">
            {t('itmReport.title')}
            <HelpTooltip section="itm-report" text={t('itmReport.helpTooltip')} />
          </h2>
          <Button variant="ghost" size="sm" onClick={startITMTour}>
            {t('onboarding.replay.btn')}
          </Button>
        </div>
        <p className="text-sm text-slate-600 dark:text-slate-300 mb-6">
          {t('itmReport.intro1')} <strong>{t('itmReport.introBold')}</strong>{' '}
          {t('itmReport.intro2')} {t('itmReport.intro3')}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div
            className="rounded-xl border-2 border-primary-200 dark:border-primary-700 bg-primary-50 dark:bg-primary-950/50 p-5 flex flex-col"
            data-tour="itm-preview"
          >
            <h3 className="font-semibold text-primary-900 dark:text-primary-200 mb-1">
              {t('itmReport.previewTitle')}
            </h3>
            <p className="text-xs text-primary-700 dark:text-primary-300 mb-4 flex-1">
              {t('itmReport.previewDesc')}
            </p>
            <Button variant="outline" onClick={handleDownloadPDF} loading={loadingPdf}>
              {t('itmReport.downloadPreview')}
            </Button>
          </div>

          <div
            className="rounded-xl border-2 border-accent-200 dark:border-accent-700 bg-accent-50 dark:bg-accent-950/50 p-5 flex flex-col"
            data-tour="itm-official"
          >
            <h3 className="font-semibold text-accent-900 dark:text-accent-300 mb-1">
              {t('itmReport.zipTitle')}
            </h3>
            <p className="text-xs text-accent-700 dark:text-accent-300 mb-4 flex-1">
              {t('itmReport.zipDesc')}
            </p>
            <Button variant="outline" onClick={handleDownloadZip} loading={loadingZip}>
              {t('itmReport.downloadZip')}
            </Button>
          </div>
        </div>
      </Card>

      {/* Rapoarte oficiale — snapshot-uri cu dublă semnătură */}
      <Card>
        {(() => {
          const signedCount = reports.filter((r) => r.status === 'SIGNED').length;
          const pendingCount = reports.length - signedCount;
          return (
            <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
              <button
                type="button"
                onClick={() => setReportsExpanded((v) => !v)}
                className="flex items-start gap-2 text-left flex-1 min-w-0 hover:opacity-80 transition"
              >
                <svg
                  className={`w-5 h-5 mt-0.5 text-slate-400 dark:text-slate-400 transition-transform flex-shrink-0 ${reportsExpanded ? 'rotate-90' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
                <div className="flex-1 min-w-0">
                  <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                    {t('itmReport.officialTitle')}
                    {reports.length > 0 && (
                      <span className="ml-2 text-sm font-normal text-slate-500 dark:text-slate-400">
                        ({reports.length})
                      </span>
                    )}
                  </h2>
                  {reportsExpanded ? (
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      {t('itmReport.officialDesc')}
                    </p>
                  ) : (
                    reports.length > 0 && (
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                        {signedCount > 0 && (
                          <span className="text-accent-700 dark:text-accent-300 font-medium">
                            {t('itmReport.signedCount', { count: signedCount })}
                          </span>
                        )}
                        {signedCount > 0 && pendingCount > 0 && ' · '}
                        {pendingCount > 0 && (
                          <span className="text-warning-700 dark:text-warning-300 font-medium">
                            {t('itmReport.pendingCount', { count: pendingCount })}
                          </span>
                        )}
                        {t('itmReport.clickToExpand')}
                      </p>
                    )
                  )}
                </div>
              </button>
              {canInitiateReport && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleGenerateOfficial}
                  loading={loadingGenerate}
                >
                  <svg
                    className="w-4 h-4 mr-1.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                  </svg>
                  {t('itmReport.generateOfficial')}
                </Button>
              )}
            </div>
          );
        })()}

        {reportsExpanded &&
          (loadingReports ? (
            <p className="text-sm text-slate-500 dark:text-slate-400 py-4">{t('common.loading')}</p>
          ) : reports.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400 py-4 text-center italic">
              {t('itmReport.noReports')}{' '}
              {canInitiateReport
                ? t('itmReport.pressToCreate')
                : t('itmReport.willCreateWhenNeeded')}
            </p>
          ) : (
            <div className="space-y-2">
              {reports.map((r) => {
                const status = r.status;
                const statusColor =
                  status === 'SIGNED' ? 'green' : status === 'PENDING_ADMIN' ? 'yellow' : 'orange';
                const statusLabel =
                  status === 'SIGNED'
                    ? t('itmReport.statusSigned')
                    : status === 'PENDING_ADMIN'
                      ? t('itmReport.statusPendingAdmin')
                      : t('itmReport.statusPendingSSM');

                const canSignThis =
                  (canSignAsSSM && !r.ssmSignerId) || (canSignAsAdmin && !r.adminSignerId);

                // Șterge: doar raport nesemnat complet + user e inițiator sau SUPER_ADMIN
                const hasAnySignature = !!(r.ssmSignerId || r.adminSignerId);
                const canDelete = !hasAnySignature && (isSuperAdmin || r.createdById === user?.id);

                const daysWaiting = status !== 'SIGNED' ? daysSince(r.createdAt) : null;

                return (
                  <div
                    key={r.id}
                    className="flex flex-wrap items-center justify-between gap-3 p-3 border border-slate-200 dark:border-slate-800 rounded-lg bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-sm font-semibold text-slate-900 dark:text-slate-100">
                          {r.reportNumber}
                        </span>
                        <Badge color={statusColor}>{statusLabel}</Badge>
                        {daysWaiting !== null && daysWaiting > 0 && (
                          <span
                            className={`text-xs font-medium ${daysWaiting >= 7 ? 'text-danger-600 dark:text-danger-300' : daysWaiting >= 3 ? 'text-warning-600 dark:text-warning-300' : 'text-slate-500 dark:text-slate-400'}`}
                          >
                            {daysWaiting === 1
                              ? t('itmReport.daysWaitingOne')
                              : t('itmReport.daysWaitingMany', { count: daysWaiting })}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                        {t('itmReport.createdLabel')}: {formatDateTime(r.createdAt)}
                        {r.ssmSignerName && (
                          <span>
                            {' '}
                            · SSM: <strong>{r.ssmSignerName}</strong> ({formatDate(r.ssmSignedAt)})
                          </span>
                        )}
                        {r.adminSignerName && (
                          <span>
                            {' '}
                            · Admin: <strong>{r.adminSignerName}</strong> (
                            {formatDate(r.adminSignedAt)})
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDownloadReport(r.id, r.reportNumber)}
                      >
                        {t('itmReport.download')}
                      </Button>
                      {canSignThis && (
                        <Button
                          variant="accent"
                          size="sm"
                          onClick={() => handleSignReport(r.id)}
                          loading={signingId === r.id}
                        >
                          {t('itmReport.sign')}
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteTargetId(r.id)}
                          className="text-danger-600 dark:text-danger-300 hover:text-danger-700 hover:bg-danger-50"
                        >
                          {t('itmReport.delete')}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
      </Card>

      <ConfirmModal
        open={!!deleteTargetId}
        onClose={() => setDeleteTargetId(null)}
        onConfirm={handleDeleteReport}
        title={t('itmReport.deleteTitle')}
        message={t('itmReport.deleteMsg')}
        confirmLabel={t('itmReport.delete')}
        cancelLabel={t('common.cancel')}
        variant="danger"
      />

      {pendingSignReportId && (
        <SignPasswordModal
          title={t('itmReport.signTitle') || 'Semnează raportul ITM'}
          description={
            t('itmReport.signDesc') ||
            'Vei semna electronic raportul oficial către Inspectoratul Teritorial de Muncă. Semnătura va fi aplicată conform Reg. UE 910/2014 (AdES).'
          }
          onSubmit={submitSignReport}
          onClose={() => setPendingSignReportId(null)}
        />
      )}

      <div className="p-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-800 rounded-lg text-xs text-slate-600 dark:text-slate-300">
        <p className="font-semibold text-slate-700 dark:text-slate-300 mb-2">
          {t('itmReport.whatIncluded')}
        </p>
        <ul className="space-y-1 list-disc pl-5">
          <li>{t('itmReport.includesCompany')}</li>
          <li>{t('itmReport.includesPeople')}</li>
          <li>{t('itmReport.includesCompliance')}</li>
          <li>
            <Trans
              i18nKey="itmReport.includesSlots"
              components={{ 1: <strong />, 3: <strong /> }}
            />
          </li>
          <li>
            <Trans i18nKey="itmReport.includesZipNote" components={{ 1: <strong /> }} />
          </li>
        </ul>
        <p className="mt-2 italic">{t('itmReport.supportDocsHint')}</p>
      </div>
    </div>
  );
};

// Sub-componentă: card toggle pentru un serviciu (SSM sau SU) cu confirmare
const ServiceToggleCard = ({
  label,
  sublabel,
  active,
  activeColor,
  otherActive,
  service,
  companyId,
  onToggled,
  showToast,
}) => {
  const { t } = useTranslation();
  const [showConfirm, setShowConfirm] = useState(false);
  const [impact, setImpact] = useState(null);
  const [loadingImpact, setLoadingImpact] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleClick = async () => {
    const nextState = !active;

    // Activare: confirmare simplă, fără impact
    if (nextState) {
      setImpact(null);
      setShowConfirm(true);
      return;
    }

    // Dezactivare: verificăm impactul
    if (!otherActive) {
      showToast(t('services.atLeastOne'), 'warning');
      return;
    }

    setLoadingImpact(true);
    try {
      const res = await companiesAPI.getServiceImpact(companyId, service);
      setImpact(res.data);
    } catch (err) {
      setImpact({ activeTrainings: 0, totalAssignments: 0 });
    } finally {
      setLoadingImpact(false);
      setShowConfirm(true);
    }
  };

  const handleConfirm = async () => {
    setSaving(true);
    try {
      const payload = service === 'SSM' ? { offersSSM: !active } : { offersSU: !active };
      const res = await companiesAPI.update(companyId, payload);
      setShowConfirm(false);
      onToggled(!active, res.data.archivedTrainings);
    } catch (err) {
      showToast(err.response?.data?.error || t('services.errorGeneric'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const borderColor = active
    ? `border-${activeColor}-600`
    : 'border-slate-200 dark:border-slate-800';
  const bgColor = active
    ? `bg-${activeColor}-50 dark:bg-${activeColor}-950/50`
    : 'bg-white dark:bg-slate-800 hover:border-slate-300';
  const checkColor = active ? `text-${activeColor}-600` : 'text-slate-300 dark:text-slate-600';

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        disabled={loadingImpact}
        className={`p-4 rounded-xl border-2 text-left transition flex items-start gap-3 ${borderColor} ${bgColor}`}
      >
        <input
          type="checkbox"
          checked={active}
          onChange={() => {}}
          className={`h-4 w-4 mt-0.5 ${checkColor} rounded pointer-events-none`}
        />
        <div>
          <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{label}</p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{sublabel}</p>
        </div>
      </button>

      {showConfirm && (
        <ConfirmModal
          open={showConfirm}
          onClose={() => setShowConfirm(false)}
          title={
            active
              ? t('services.deactivateTitle', { service })
              : t('services.activateTitle', { service })
          }
          message={
            active ? (
              <div className="space-y-3">
                <p className="text-sm text-slate-700 dark:text-slate-300">
                  <Trans
                    i18nKey="services.confirmDeactivate"
                    values={{ label }}
                    components={{ 1: <strong /> }}
                  />
                </p>
                {impact && impact.activeTrainings > 0 && (
                  <div className="bg-danger-50 dark:bg-danger-950/50 border border-danger-200 dark:border-danger-700 rounded-lg p-3 text-sm text-danger-800 dark:text-danger-300">
                    <p className="font-semibold">{t('services.warningLabel')}</p>
                    <p className="mt-1">
                      {t('services.deactivateImpact', { count: impact.activeTrainings, service })}
                    </p>
                  </div>
                )}
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {t('services.deactivateDetails', {
                    service,
                    ipType: service === 'SSM' ? 'SSM' : 'SU',
                  })}
                </p>
              </div>
            ) : (
              <p className="text-sm text-slate-700 dark:text-slate-300">
                {t('services.activateDesc', { label, service })}
              </p>
            )
          }
          confirmLabel={active ? t('services.deactivate') : t('services.activate')}
          variant={active ? 'danger' : 'primary'}
          loading={saving}
          onConfirm={handleConfirm}
        />
      )}
    </>
  );
};

// Toggle pentru add-on-ul Raport ITM. Spre deosebire de ServiceToggleCard (SSM/SU), nu există
// impact de arhivare (ITM nu produce instruiri) și nu există regula „cel puțin unul activ" —
// e un serviciu opțional, plătit separat. Doar SUPER_ADMIN îl poate comuta (decizie de facturare).
const ITMServiceToggleCard = ({ active, companyId, onToggled, showToast }) => {
  const { t } = useTranslation();
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleConfirm = async () => {
    setSaving(true);
    try {
      const res = await companiesAPI.update(companyId, { offersITM: !active });
      setShowConfirm(false);
      onToggled(res.data.offersITM ?? !active);
    } catch (err) {
      showToast(err.response?.data?.error || t('services.errorGeneric'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setShowConfirm(true)}
        className={`w-full p-4 rounded-xl border-2 text-left transition flex items-start gap-3 ${
          active
            ? 'border-violet-500 dark:border-violet-600 bg-violet-50 dark:bg-violet-950/40'
            : 'border-dashed border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-violet-300'
        }`}
      >
        <input
          type="checkbox"
          checked={active}
          onChange={() => {}}
          className={`h-4 w-4 mt-0.5 ${active ? 'text-violet-600' : 'text-slate-300 dark:text-slate-600'} rounded pointer-events-none`}
        />
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              {t('itmService.cardTitle')}
            </p>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-violet-100 dark:bg-violet-900/60 text-violet-700 dark:text-violet-200">
              {t('itmService.addonBadge')}
            </span>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            {t('itmService.cardSublabel')}
          </p>
        </div>
      </button>

      {showConfirm && (
        <ConfirmModal
          open={showConfirm}
          onClose={() => setShowConfirm(false)}
          title={active ? t('itmService.deactivateTitle') : t('itmService.activateTitle')}
          message={
            <p className="text-sm text-slate-700 dark:text-slate-300">
              {active ? t('itmService.confirmDeactivate') : t('itmService.confirmActivate')}
            </p>
          }
          confirmLabel={active ? t('services.deactivate') : t('services.activate')}
          variant={active ? 'danger' : 'primary'}
          loading={saving}
          onConfirm={handleConfirm}
        />
      )}
    </>
  );
};

const EmailToggleCard = ({ active, companyId, onToggled, showToast }) => {
  const { t } = useTranslation();
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleConfirm = async () => {
    setSaving(true);
    try {
      await companiesAPI.update(companyId, { emailsEnabled: !active });
      setShowConfirm(false);
      onToggled(!active);
    } catch (err) {
      showToast(err.response?.data?.error || t('emailToggle.error'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const borderColor = active ? 'border-blue-600' : 'border-slate-200 dark:border-slate-800';
  const bgColor = active
    ? 'bg-blue-50 dark:bg-blue-950/50'
    : 'bg-white dark:bg-slate-800 hover:border-slate-300';
  const checkColor = active ? 'text-blue-600' : 'text-slate-300 dark:text-slate-600';

  return (
    <Card>
      <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-1">
        {t('emailToggle.title')}
      </h2>
      <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
        {t('emailToggle.description')}
      </p>
      <button
        type="button"
        onClick={() => setShowConfirm(true)}
        className={`w-full p-4 rounded-xl border-2 text-left transition flex items-start gap-3 ${borderColor} ${bgColor}`}
      >
        <input
          type="checkbox"
          checked={active}
          onChange={() => {}}
          className={`h-4 w-4 mt-0.5 ${checkColor} rounded pointer-events-none`}
        />
        <div>
          <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            {t('emailToggle.label')}
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            {t('emailToggle.sublabel')}
          </p>
        </div>
      </button>
      {showConfirm && (
        <ConfirmModal
          open={showConfirm}
          onClose={() => setShowConfirm(false)}
          title={active ? t('emailToggle.deactivateTitle') : t('emailToggle.activateTitle')}
          message={active ? t('emailToggle.confirmDeactivate') : t('emailToggle.confirmActivate')}
          confirmLabel={active ? t('emailToggle.deactivate') : t('emailToggle.activate')}
          variant={active ? 'danger' : 'primary'}
          loading={saving}
          onConfirm={handleConfirm}
        />
      )}
    </Card>
  );
};

const CompanyDetails = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [company, setCompany] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'departments');

  // Sincronizare tab cu query string — important pentru navigarea declanșată din wizard
  // (când URL-ul se schimbă din `?tab=employees` în `?tab=itm`, tab-ul afișat trebuie să urmeze).
  // Fără asta, `useState` reține doar valoarea de la mount și tab-urile par „blocate".
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && tab !== activeTab) setActiveTab(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Administrators state
  const [admins, setAdmins] = useState([]);
  const [delegations, setDelegations] = useState([]);

  // Add admin modal — două flow-uri: promovează user existent SAU creează cont nou.
  const [showAddAdminModal, setShowAddAdminModal] = useState(false);
  const [adminModalTab, setAdminModalTab] = useState('promote'); // 'promote' | 'create'
  const [, setAdminFormData] = useState({ email: '', firstName: '', lastName: '' });
  const [, setAdminFormError] = useState(null);
  const [adminSuccess, setAdminSuccess] = useState(null);
  // Promote tab state
  const [promoteCandidateId, setPromoteCandidateId] = useState('');
  const [promoteSaving, setPromoteSaving] = useState(false);
  const [promoteError, setPromoteError] = useState(null);

  // Transfer responsibility modal
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferTargetId, setTransferTargetId] = useState('');
  const [transferSaving, setTransferSaving] = useState(false);

  // Delegation modal
  const [showDelegationModal, setShowDelegationModal] = useState(false);
  const [delegationForm, setDelegationForm] = useState({
    delegateeId: '',
    startDate: '',
    endDate: '',
    reason: '',
  });
  const [delegationSaving, setDelegationSaving] = useState(false);
  const [delegationError, setDelegationError] = useState(null);

  // Revoke confirm
  const [revokingDelegation, setRevokingDelegation] = useState(null);

  const fetchCompany = useCallback(async () => {
    try {
      const response = await api.get(`/companies/${companyId}`);
      setCompany(response.data);
    } catch (err) {
      console.error(err);
      showToast(t('companyDetails.loadError'), 'error');
      navigate('/companies');
    } finally {
      setLoading(false);
    }
  }, [companyId, navigate]);

  const fetchDepartments = useCallback(async () => {
    try {
      const res = await departmentsAPI.getAll(companyId);
      setDepartments(res.data);
    } catch (err) {
      console.error('Eroare departamente:', err);
    }
  }, [companyId]);

  const fetchDelegations = useCallback(async () => {
    try {
      const res = await delegationsAPI.list(companyId);
      setDelegations(res.data);
    } catch (err) {
      console.error('Eroare delegari:', err);
    }
  }, [companyId]);

  useEffect(() => {
    fetchCompany();
    fetchDepartments();
  }, [fetchCompany, fetchDepartments]);

  useEffect(() => {
    if (company) {
      setAdmins(
        company.users.filter((u) => ['COMPANY_ADMIN', 'SSM_COMPANY_ADMIN'].includes(u.role))
      );
      fetchDelegations();
    }
  }, [company, fetchDelegations]);

  // Admin handlers
  const [transferError, setTransferError] = useState(null);

  // Promovare user existent → administrator (folosește POST /api/employees/:id/change-role).
  // Targetul e un employee (nu user) — endpoint-ul operează pe employee.id.
  const handlePromoteExisting = async () => {
    if (!promoteCandidateId) {
      setPromoteError(t('companyDetails.selectEmployeeError'));
      return;
    }
    setPromoteSaving(true);
    setPromoteError(null);
    try {
      const newRole = company.type === 'SSM' ? 'SSM_COMPANY_ADMIN' : 'COMPANY_ADMIN';
      await api.post(`/employees/${promoteCandidateId}/change-role`, { newRole });
      await fetchCompany();
      showToast(t('companyDetails.employeePromoted'), 'success');
      setShowAddAdminModal(false);
      setPromoteCandidateId('');
    } catch (err) {
      setPromoteError(err.response?.data?.error || t('companyDetails.promoteErrorMsg'));
    } finally {
      setPromoteSaving(false);
    }
  };

  const handleTransfer = async () => {
    if (!transferTargetId) return;
    setTransferSaving(true);
    setTransferError(null);
    try {
      await delegationsAPI.transferResponsibility(companyId, transferTargetId);
      setShowTransferModal(false);
      setTransferTargetId('');
      fetchCompany();
    } catch (err) {
      setTransferError(
        err.response?.data?.error || err.message || t('companyDetails.transferErrorMsg')
      );
    } finally {
      setTransferSaving(false);
    }
  };

  const handleCreateDelegation = async () => {
    if (!delegationForm.delegateeId || !delegationForm.startDate || !delegationForm.endDate) {
      setDelegationError(t('companyDetails.requiredFieldsError'));
      return;
    }
    setDelegationSaving(true);
    setDelegationError(null);
    try {
      await delegationsAPI.create(companyId, delegationForm);
      setShowDelegationModal(false);
      setDelegationForm({ delegateeId: '', startDate: '', endDate: '', reason: '' });
      fetchDelegations();
    } catch (err) {
      setDelegationError(err.response?.data?.error || t('companyDetails.delegationErrorMsg'));
    } finally {
      setDelegationSaving(false);
    }
  };

  const handleRevokeDelegation = async () => {
    if (!revokingDelegation) return;
    try {
      await delegationsAPI.revoke(companyId, revokingDelegation.id);
      setRevokingDelegation(null);
      fetchDelegations();
    } catch (err) {
      showToast(err.response?.data?.error || t('companyDetails.revokeErrorMsg'), 'error');
      setRevokingDelegation(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton variant="title" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Skeleton variant="stat-card" />
          <Skeleton variant="stat-card" />
          <Skeleton variant="stat-card" />
          <Skeleton variant="stat-card" />
        </div>
        <Skeleton variant="card" />
      </div>
    );
  }

  // Dacă încărcarea s-a terminat dar firma nu există (fetch eșuat / sesiune expirată),
  // fetchCompany face deja navigate('/companies'); evităm crash-ul în randarea intermediară.
  if (!company) return null;

  // Butonul "Inapoi" duce unde trebuie
  const backPath = company?.type === 'SSM' ? '/ssm-companies' : '/companies';
  const backLabel =
    company?.type === 'SSM'
      ? t('companyDetails.backToSSMCompanies')
      : t('companyDetails.backToClientCompanies');

  // Statistici corecte
  const sheetsPendingSignature = company.stats?.sheetsPendingSignature ?? 0;

  // Permisiune tab „Raport ITM" — match cu logica din backend canAccessITMReport.
  // Vizibil doar pe firme CLIENT (SSM-urile nu au raport de compliance propriu în acest UI).
  const canSeeITMReport = (() => {
    if (!company || !user) return false;
    if (company.type !== 'CLIENT') return false;
    if (user.role === 'SUPER_ADMIN') return true;
    const isExt = !!company.parentCompanyId;
    const isInt = !company.parentCompanyId;
    if (isExt) {
      if (
        user.companyId === company.parentCompanyId &&
        (['SSM_COMPANY_ADMIN', 'SSM_INSTRUCTOR'].includes(user.role) || user.isCadruTehnicPSI)
      )
        return true;
      if (user.companyId === company.id && user.role === 'COMPANY_ADMIN') return true;
    }
    if (isInt && user.companyId === company.id) {
      if (user.role === 'COMPANY_ADMIN') return true;
      if (user.isSSMInstructor || user.isCadruTehnicPSI) return true;
    }
    return false;
  })();

  // Permisiune tab „Conducători (CLM)" — administrat de firma SSM (firma proprie + clienții ei).
  // Match cu backend `canManageCompanyClm`.
  const canManageClm = (() => {
    if (!company || !user) return false;
    if (user.role === 'SUPER_ADMIN') return true;
    if (!['SSM_COMPANY_ADMIN', 'SSM_INSTRUCTOR'].includes(user.role)) return false;
    return user.companyId === company.id || user.companyId === company.parentCompanyId;
  })();

  // Filtrare angajati: doar cei ai acestei firme (nu din SSM)
  const ownEmployees = (company.employees || []).filter(
    (emp) => emp.companyId === companyId || !emp.companyId
  );
  // „Neatribuit" = fără departament direct ȘI nu e CLM (manager) pe niciun departament al firmei.
  // CLM-ul e considerat atribuit la departamentul/departamentele pe care le manageriază.
  const unassignedEmployees = ownEmployees.filter(
    (emp) => !emp.departmentId && !(emp.managedDepartments && emp.managedDepartments.length > 0)
  );
  // Dacă firma nu are niciun departament, angajații nu au unde fi alocați — nu-i considerăm „nealocați".
  const unassignedCount = departments.length > 0 ? unassignedEmployees.length : 0;
  const hasActionableStats = unassignedCount > 0 || sheetsPendingSignature > 0;

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={[
          { label: t('companyDetails.breadcrumbCompanies'), href: '/companies' },
          { label: company.name || t('companyDetails.breadcrumbDetails') },
        ]}
      />
      {/* Header */}
      <PageHeader
        title={company.name}
        subtitle={`CUI: ${company.cui}`}
        actions={
          <>
            <Button variant="ghost" size="sm" onClick={() => navigate(backPath)}>
              <ArrowLeftIcon /> {backLabel}
            </Button>
          </>
        }
      >
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <Badge color={company.type === 'SSM' ? 'blue' : 'green'}>{company.type}</Badge>
          <StatusBadge status={company.active ? 'active' : 'inactive'} />
          {/* Servicii active — la firme client arată ce arii sunt active */}
          {company.type === 'CLIENT' && (
            <>
              {company.offersSSM && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 dark:bg-slate-800/60 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-slate-700">
                  SSM
                </span>
              )}
              {company.offersSU && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-orange-50 dark:bg-slate-800/60 text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-slate-700">
                  SU/PSI
                </span>
              )}
              {company.offersITM && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-violet-50 dark:bg-slate-800/60 text-violet-700 dark:text-violet-300 border border-violet-200 dark:border-slate-700">
                  {t('itmService.badge')}
                </span>
              )}
              {company.parentCompanyId ? (
                <span className="text-xs text-slate-500 dark:text-slate-400 ml-1">
                  · {t('companyDetails.externalSSM')}
                </span>
              ) : (
                <span className="text-xs text-slate-500 dark:text-slate-400 ml-1">
                  · {t('companyDetails.internalSSM')}
                </span>
              )}
            </>
          )}
        </div>
      </PageHeader>

      {/* Actionable Stat Cards — only surface items that need attention */}
      {hasActionableStats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {unassignedCount > 0 && (
            <StatCard
              label={t('companyDetails.unassignedEmployeesStat')}
              value={unassignedCount}
              icon={<UsersIcon />}
            />
          )}
          {sheetsPendingSignature > 0 && (
            <StatCard
              label={t('companyDetails.sheetsToSignStat')}
              value={sheetsPendingSignature}
              icon={<DocumentIcon />}
            />
          )}
        </div>
      )}

      <Tabs
        tabs={[
          {
            id: 'departments',
            label: t('companyDetails.tabs.departments'),
            count: departments.length,
          },
          {
            id: 'employees',
            label: t('companyDetails.tabs.employees'),
            hint:
              unassignedCount > 0
                ? t('companyDetails.unassignedEmployees', { count: unassignedCount })
                : `${ownEmployees.length}`,
            hintColor: unassignedCount > 0 ? 'warning' : undefined,
          },
          { id: 'assign', label: t('companyDetails.tabs.assign') },
          {
            id: 'sheets',
            label: t('companyDetails.tabs.sheets'),
            hint:
              sheetsPendingSignature > 0
                ? t('companyDetails.sheetsToSign', { count: sheetsPendingSignature })
                : undefined,
            hintColor: sheetsPendingSignature > 0 ? 'warning' : undefined,
          },
          { id: 'documents', label: t('companyDetails.tabs.documents') },
          ...(canSeeITMReport ? [{ id: 'itm', label: t('companyDetails.tabs.itm') }] : []),
          ...(canManageClm ? [{ id: 'clm', label: t('companyDetails.tabs.clm') }] : []),
          { id: 'admins', label: t('companyDetails.tabs.admins'), count: admins.length },
          { id: 'info', label: t('companyDetails.tabs.info') },
        ]}
        activeTab={activeTab}
        onChange={setActiveTab}
      />

      {activeTab === 'info' && (
        <>
          {/* Informatii Firma */}
          <Card>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
              {t('companyDetails.info')}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">{t('common.address')}</p>
                <p className="font-medium text-slate-900 dark:text-slate-100">
                  {company.address || 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">{t('common.phone')}</p>
                <p className="font-medium text-slate-900 dark:text-slate-100">
                  {company.phone || 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">{t('common.email')}</p>
                <p className="font-medium text-slate-900 dark:text-slate-100">
                  {company.email || 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">{t('common.date')}</p>
                <p className="font-medium text-slate-900 dark:text-slate-100">
                  {company.createdAt ? formatDate(company.createdAt) : 'N/A'}
                </p>
              </div>
            </div>
          </Card>

          {/* Branding / Firma mea — white-label self-service (logo light/dark + nume brand),
                cu gating premium și toggle SUPER_ADMIN. */}
          <Card>
            <BrandingManager
              company={company}
              onChange={(next) => setCompany((prev) => ({ ...prev, ...next }))}
            />
          </Card>

          {/* Email toggle — doar SUPER_ADMIN */}
          {user?.role === 'SUPER_ADMIN' && (
            <EmailToggleCard
              active={company.emailsEnabled}
              companyId={companyId}
              onToggled={(next) => {
                setCompany((prev) => ({ ...prev, emailsEnabled: next }));
                showToast(
                  next ? t('emailToggle.activated') : t('emailToggle.deactivated'),
                  'success'
                );
              }}
              showToast={showToast}
            />
          )}

          {/* Servicii active — editabile doar de:
                - SUPER_ADMIN (oricând, pentru orice firmă CLIENT)
                - User din firma SSM părinte care e SSM_COMPANY_ADMIN, SSM_INSTRUCTOR
                  sau are isCadruTehnicPSI (doar pentru CLIENT cu parent SSM) */}
          {company.type === 'CLIENT' &&
            (user?.role === 'SUPER_ADMIN' ||
              (company.parentCompanyId &&
                user?.companyId === company.parentCompanyId &&
                (['SSM_COMPANY_ADMIN', 'SSM_INSTRUCTOR'].includes(user?.role) ||
                  user?.isCadruTehnicPSI === true))) && (
              <Card>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-1">
                  {t('companyDetails.servicesTitle')}
                </h2>
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
                  {t('companyDetails.servicesDesc')}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <ServiceToggleCard
                    label={t('services.ssmLabel')}
                    sublabel={t('services.ssmSublabel')}
                    active={company.offersSSM}
                    activeColor="primary"
                    otherActive={company.offersSU}
                    service="SSM"
                    companyId={companyId}
                    onToggled={(next, archived) => {
                      setCompany((prev) => ({ ...prev, offersSSM: next }));
                      const base = next ? t('services.ssmActivated') : t('services.ssmDeactivated');
                      const msg =
                        !next && archived
                          ? t('services.withArchived', { base, count: archived })
                          : base;
                      showToast(msg, 'success');
                    }}
                    showToast={showToast}
                  />
                  <ServiceToggleCard
                    label={t('services.suLabel')}
                    sublabel={t('services.suSublabel')}
                    active={company.offersSU}
                    activeColor="warning"
                    otherActive={company.offersSSM}
                    service="SU"
                    companyId={companyId}
                    onToggled={(next, archived) => {
                      setCompany((prev) => ({ ...prev, offersSU: next }));
                      const base = next ? t('services.suActivated') : t('services.suDeactivated');
                      const msg =
                        !next && archived
                          ? t('services.withArchived', { base, count: archived })
                          : base;
                      showToast(msg, 'success');
                    }}
                    showToast={showToast}
                  />
                </div>

                {/* Raport ITM (add-on) — doar SUPER_ADMIN poate comuta (decizie de facturare).
                    Pentru ceilalți admini (SSM părinte) e ascuns: nu au drept pe acest serviciu. */}
                {user?.role === 'SUPER_ADMIN' && (
                  <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800">
                    <ITMServiceToggleCard
                      active={company.offersITM}
                      companyId={companyId}
                      onToggled={(next) => {
                        setCompany((prev) => ({ ...prev, offersITM: next }));
                        showToast(
                          next ? t('itmService.activated') : t('itmService.deactivated'),
                          'success'
                        );
                      }}
                      showToast={showToast}
                    />
                  </div>
                )}
              </Card>
            )}
        </>
      )}

      {activeTab === 'departments' && (
        /* Departamente */
        <Card>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              {t('companyDetails.departmentsTitle', { count: departments.length })}
            </h2>
            <Link to={`/departments?companyId=${companyId}`}>
              <Button variant="primary" size="sm">
                <BuildingIcon /> {t('companyDetails.manageDepartments')}
              </Button>
            </Link>
          </div>

          {departments.length === 0 ? (
            <EmptyState
              icon={<BuildingIcon />}
              title={t('companyDetails.noDepartments')}
              description={t('companyDetails.noDepartmentsDesc')}
              actionLabel={t('companyDetails.addDepartments')}
              onAction={() => navigate(`/departments?companyId=${companyId}`)}
            />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {departments.map((dept) => (
                <div
                  key={dept.id}
                  className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg"
                >
                  <div className="w-10 h-10 rounded-lg bg-primary-50 dark:bg-primary-950/50 flex items-center justify-center text-primary-600 dark:text-primary-300 flex-shrink-0">
                    <BuildingIcon />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-slate-900 dark:text-slate-100 text-sm truncate">
                      {dept.name}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {t('companyDetails.employeesCount', { count: dept._count?.employees || 0 })}
                      {dept.manager
                        ? ` · ${dept.manager.firstName} ${dept.manager.lastName}`
                        : ` · ${t('companyDetails.noManager')}`}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {activeTab === 'admins' && (
        /* Administratori */
        <Card>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              {t('companyDetails.adminsTitle', { count: admins.length })}
            </h2>
            <div className="flex flex-wrap gap-2">
              {/* Transfer/Imputernicire — doar COMPANY_ADMIN responsabil cu semnatura sau admin nivel firmă */}
              {(user?.role === 'COMPANY_ADMIN' && user?.isSignatureResponsible) ||
              ['SUPER_ADMIN', 'SSM_COMPANY_ADMIN'].includes(user?.role) ? (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setDelegationForm({
                        delegateeId: '',
                        startDate: '',
                        endDate: '',
                        reason: '',
                      });
                      setDelegationError(null);
                      setShowDelegationModal(true);
                    }}
                  >
                    {t('companyDetails.delegation')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setTransferTargetId('');
                      setTransferError(null);
                      setShowTransferModal(true);
                    }}
                  >
                    {t('companyDetails.transferResponsibility')}
                  </Button>
                </>
              ) : null}
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  setAdminFormData({ email: '', firstName: '', lastName: '' });
                  setAdminFormError(null);
                  setAdminSuccess(null);
                  setAdminModalTab('promote');
                  setPromoteCandidateId('');
                  setPromoteError(null);
                  setShowAddAdminModal(true);
                }}
              >
                <PlusIcon /> {t('companyDetails.addAdministrator')}
              </Button>
            </div>
          </div>

          {admins.length === 0 ? (
            <EmptyState
              icon={<KeyIcon />}
              title={t('companyDetails.noAdministrators')}
              description={t('companyDetails.noAdministratorsDesc')}
            />
          ) : (
            <div className="space-y-2">
              {admins.map((admin) => (
                <div
                  key={admin.id}
                  className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg"
                >
                  <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/50 flex items-center justify-center text-primary-700 dark:text-primary-300 font-semibold text-sm flex-shrink-0">
                    {admin.firstName[0]}
                    {admin.lastName[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 dark:text-slate-100 text-sm">
                      {admin.firstName} {admin.lastName}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{admin.email}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {admin.isSignatureResponsible && (
                      <Badge color="green">{t('companyDetails.signatureResponsibleBadge')}</Badge>
                    )}
                    <StatusBadge status={admin.active ? 'active' : 'inactive'} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Delegations sub-section */}
          {delegations.length > 0 && (
            <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-800">
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
                {t('companyDetails.delegations')}
              </h3>
              <div className="space-y-2">
                {delegations.map((d) => (
                  <div
                    key={d.id}
                    className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 dark:text-slate-100">
                        {d.delegator.firstName} {d.delegator.lastName} &rarr;{' '}
                        {d.delegatee.firstName} {d.delegatee.lastName}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {new Date(d.startDate).toLocaleDateString('ro-RO')} —{' '}
                        {new Date(d.endDate).toLocaleDateString('ro-RO')}
                        {d.reason && ` · ${d.reason}`}
                      </p>
                    </div>
                    <Badge
                      color={
                        d.status === 'active' ? 'green' : d.status === 'pending' ? 'yellow' : 'gray'
                      }
                    >
                      {d.status === 'active'
                        ? t('companyDetails.active')
                        : d.status === 'pending'
                          ? t('companyDetails.pending')
                          : t('companyDetails.expired')}
                    </Badge>
                    {d.status !== 'expired' && (
                      <button
                        onClick={() => setRevokingDelegation(d)}
                        className="text-xs text-danger-600 dark:text-danger-300 hover:text-danger-700 font-medium"
                      >
                        {t('companyDetails.revoke')}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}

      {activeTab === 'employees' && <Employees scopedCompanyId={companyId} />}

      {activeTab === 'assign' && <AssignCourses scopedCompanyId={companyId} />}

      {activeTab === 'sheets' && <EmployeeSheets scopedCompanyId={companyId} />}

      {activeTab === 'documents' && <CompanyDocuments scopedCompanyId={companyId} />}

      {activeTab === 'itm' && (
        <ITMReportTab company={company} companyId={companyId} showToast={showToast} user={user} />
      )}

      {activeTab === 'clm' && canManageClm && <ClmManagement companyId={companyId} />}
      {/* Add Admin Modal — două flow-uri în tab-uri:
                  • Promovează existent: alege un angajat din firmă și îi schimbă rolul în COMPANY_ADMIN.
                  • Creează nou: form clasic email/nume/prenume → cont nou cu parolă temporară. */}
      <Modal
        open={showAddAdminModal}
        onClose={() => setShowAddAdminModal(false)}
        title={t('companyDetails.addAdminModalTitle')}
        size="md"
        footer={
          adminSuccess ? (
            <div className="flex justify-end">
              <Button variant="primary" onClick={() => setShowAddAdminModal(false)}>
                {t('companyDetails.close')}
              </Button>
            </div>
          ) : adminModalTab === 'promote' ? (
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setShowAddAdminModal(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={handlePromoteExisting}
                loading={promoteSaving}
                disabled={!promoteCandidateId}
              >
                {t('companyDetails.promoteToAdmin')}
              </Button>
            </div>
          ) : (
            // Tab „Creează nou" — redirect la formularul complet de angajat cu rol prefixat
            // ca să avem toate câmpurile (CNP, semnătură, flag-uri, etc.) la creare.
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setShowAddAdminModal(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  const adminRole = company.type === 'SSM' ? 'SSM_COMPANY_ADMIN' : 'COMPANY_ADMIN';
                  setShowAddAdminModal(false);
                  navigate(`/employees/new?companyId=${companyId}&role=${adminRole}`);
                }}
              >
                {t('companyDetails.openFullForm')}
              </Button>
            </div>
          )
        }
      >
        {adminSuccess ? (
          <div className="space-y-4">
            <Alert type="success">{adminSuccess.message}</Alert>
            <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg space-y-2">
              <p className="text-sm">
                <span className="font-medium">{t('common.email')}:</span> {adminSuccess.admin.email}
              </p>
              <p className="text-sm">
                <span className="font-medium">{t('companyDetails.fullName')}:</span>{' '}
                {adminSuccess.admin.firstName} {adminSuccess.admin.lastName}
              </p>
              {adminSuccess.temporaryPassword ? (
                <p className="text-sm">
                  <span className="font-medium">{t('companyDetails.tempPassword')}:</span>{' '}
                  <code className="bg-slate-200 dark:bg-slate-700 px-2 py-0.5 rounded">
                    {adminSuccess.temporaryPassword}
                  </code>
                </p>
              ) : (
                <p className="text-sm text-accent-600 dark:text-accent-400">
                  {t('companyDetails.passwordSentEmail')}
                </p>
              )}
            </div>
            <Alert type="info">{t('companyDetails.adminMustChangePassword')}</Alert>
          </div>
        ) : (
          <div className="space-y-4">
            <Tabs
              tabs={[
                { id: 'promote', label: t('companyDetails.promoteExistingTab') },
                { id: 'create', label: t('companyDetails.createNewTab') },
              ]}
              activeTab={adminModalTab}
              onChange={setAdminModalTab}
            />

            {adminModalTab === 'promote' &&
              (() => {
                // Candidați: angajații firmei care nu sunt deja administratori.
                const candidates = (company.employees || [])
                  .filter((emp) => emp.companyId === companyId || !emp.companyId)
                  .filter((emp) => emp.user?.active !== false)
                  .filter((emp) => !['COMPANY_ADMIN', 'SSM_COMPANY_ADMIN'].includes(emp.user?.role))
                  .map((emp) => ({
                    value: emp.id,
                    label: `${emp.user.firstName} ${emp.user.lastName}`,
                    subtitle: `${emp.user.email} · ${emp.user.role}`,
                  }));
                return (
                  <div className="space-y-3">
                    {promoteError && <Alert type="error">{promoteError}</Alert>}
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                      <Trans
                        i18nKey="companyDetails.promoteHint"
                        values={{
                          role: company.type === 'SSM' ? 'SSM_COMPANY_ADMIN' : 'COMPANY_ADMIN',
                        }}
                        components={{ role: <span className="font-mono text-xs" /> }}
                      />
                    </p>
                    {candidates.length === 0 ? (
                      <Alert type="info">{t('companyDetails.noEligibleEmployees')}</Alert>
                    ) : (
                      <SearchableSelect
                        label={t('companyDetails.employeeToPromote')}
                        value={promoteCandidateId}
                        onChange={(v) => setPromoteCandidateId(v)}
                        options={candidates}
                        placeholder={t('companyDetails.searchByNameEmail')}
                      />
                    )}
                  </div>
                );
              })()}

            {adminModalTab === 'create' && (
              <div className="space-y-3">
                <Alert type="info">
                  <p className="text-sm">{t('companyDetails.createNewInfo')}</p>
                </Alert>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {t('companyDetails.pressOpenForm')}
                </p>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Transfer Responsibility Modal */}
      <Modal
        open={showTransferModal}
        onClose={() => setShowTransferModal(false)}
        title={t('companyDetails.transferModalTitle')}
        size="md"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setShowTransferModal(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleTransfer}
              loading={transferSaving}
              disabled={!transferTargetId}
            >
              {t('companyDetails.transferResponsibility')}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {transferError && <Alert type="error">{transferError}</Alert>}
          <p className="text-sm text-slate-600 dark:text-slate-300">
            {t('companyDetails.transferDesc')}
          </p>
          <SearchableSelect
            label={t('companyDetails.newResponsibleAdmin')}
            value={transferTargetId}
            onChange={(val) => setTransferTargetId(val)}
            options={admins
              .filter((a) => !a.isSignatureResponsible && a.active)
              .map((a) => ({
                value: a.id,
                label: `${a.firstName} ${a.lastName}`,
                subtitle: a.email,
              }))}
            placeholder={t('companyDetails.searchAdmin')}
          />
        </div>
      </Modal>

      {/* Delegation Modal */}
      <Modal
        open={showDelegationModal}
        onClose={() => setShowDelegationModal(false)}
        title={t('companyDetails.delegationModalTitle')}
        size="md"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setShowDelegationModal(false)}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" onClick={handleCreateDelegation} loading={delegationSaving}>
              {t('companyDetails.create')}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {delegationError && <Alert type="error">{delegationError}</Alert>}
          <SearchableSelect
            label={t('companyDetails.delegatee')}
            value={delegationForm.delegateeId}
            onChange={(val) => setDelegationForm({ ...delegationForm, delegateeId: val })}
            options={admins
              .filter((a) => a.active)
              .map((a) => ({
                value: a.id,
                label: `${a.firstName} ${a.lastName}`,
                subtitle: a.email,
              }))}
            placeholder={t('companyDetails.searchAdmin')}
          />
          <Input
            label={t('companyDetails.startDate')}
            type="date"
            value={delegationForm.startDate}
            onChange={(e) => setDelegationForm({ ...delegationForm, startDate: e.target.value })}
          />
          <Input
            label={t('companyDetails.endDate')}
            type="date"
            value={delegationForm.endDate}
            onChange={(e) => setDelegationForm({ ...delegationForm, endDate: e.target.value })}
          />
          <Input
            label={t('companyDetails.reasonOptional')}
            value={delegationForm.reason}
            onChange={(e) => setDelegationForm({ ...delegationForm, reason: e.target.value })}
            placeholder={t('companyDetails.reasonExample')}
          />
        </div>
      </Modal>

      {/* Revoke Confirm */}
      <ConfirmModal
        open={!!revokingDelegation}
        onClose={() => setRevokingDelegation(null)}
        onConfirm={handleRevokeDelegation}
        title={t('companyDetails.revokeDelegationTitle')}
        message={t('companyDetails.revokeDelegationMsg', {
          name: `${revokingDelegation?.delegatee?.firstName || ''} ${revokingDelegation?.delegatee?.lastName || ''}`,
        })}
        confirmLabel={t('companyDetails.revoke')}
        variant="danger"
      />
    </div>
  );
};

export default CompanyDetails;
